<template>
  <div class="clinical-header-wrapper">
    <v-app-bar
      app
      flat
      elevation="0"
      height="64"
      class="clinical-topbar"
      :color="$vuetify.theme.dark ? '#131B2E' : '#FFFFFF'"
    >
      <!-- LEFT SECTION: Drawer Toggle (Mobile), Active Branch Selector & Optional Title -->
      <div class="header-left-section d-flex align-center">
        <!-- Mobile Drawer Toggle Button -->
        <v-btn
          icon
          class="mr-2 d-lg-none drawer-toggle-btn"
          @click.stop="onClickBtn"
          title="Open Menu"
        >
          <v-icon size="22" color="#0D9488">mdi-menu</v-icon>
        </v-btn>

        <!-- Active Branch Selector Card / Dropdown -->
        <v-menu
          v-if="showBranchSelector && canSeeAllBranches && branches.length > 0"
          offset-y
          transition="slide-y-transition"
          content-class="branch-dropdown-menu"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="branch-selector-pill d-none d-sm-flex align-center"
              v-bind="attrs"
              v-on="on"
              role="button"
              title="Select Active Branch"
            >
              <div class="branch-icon-box mr-2">
                <v-icon size="17" color="#0D9488">mdi-hospital-building</v-icon>
              </div>
              <div class="branch-meta mr-2 text-left">
                <div class="branch-eyebrow">ACTIVE BRANCH</div>
                <div class="branch-name">{{ currentBranchName }}</div>
              </div>
              <div class="branch-chevron-box ml-1">
                <v-icon size="16" color="#94A3B8">mdi-chevron-down</v-icon>
              </div>
            </div>
          </template>

          <v-card class="branch-menu-card rounded-xl" elevation="8">
            <div class="branch-menu-header px-4 pt-3 pb-2">
              <span class="text-caption font-weight-bold text-uppercase" style="letter-spacing: 0.05em; color: #64748B;">
                Switch Branch
              </span>
            </div>
            <v-divider class="mb-1"></v-divider>
            <v-list dense class="branch-dropdown-list py-1">
              <v-list-item
                v-for="branch in branchItems"
                :key="branch.id || 'all'"
                @click="onSelectBranch(branch.id)"
                :class="{ 'active-branch-item': selectedBranchId === branch.id }"
                class="branch-menu-item mx-2 my-1 rounded-lg"
              >
                <v-list-item-icon class="mr-3 my-auto">
                  <div class="branch-item-icon-wrap" :class="{ 'active-wrap': selectedBranchId === branch.id }">
                    <v-icon small :color="selectedBranchId === branch.id ? '#0D9488' : '#64748B'">
                      {{ branch.id ? 'mdi-hospital-building' : 'mdi-domain' }}
                    </v-icon>
                  </div>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="branch-item-title" :class="{ 'font-weight-bold': selectedBranchId === branch.id }">
                    {{ branch.name }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action v-if="selectedBranchId === branch.id" class="my-auto">
                  <v-icon size="18" color="#0D9488">mdi-check-circle</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>

        <!-- Single Fixed Branch Badge -->
        <div
          v-else-if="showBranchSelector && !canSeeAllBranches && userBranch"
          class="branch-selector-pill d-none d-sm-flex align-center"
        >
          <div class="branch-icon-box mr-2">
            <v-icon size="17" color="#0D9488">mdi-hospital-building</v-icon>
          </div>
          <div class="branch-meta text-left">
            <div class="branch-eyebrow">ACTIVE BRANCH</div>
            <div class="branch-name">{{ userBranch.name }}</div>
          </div>
        </div>

        <!-- Optional Page Title (when not on dashboard) -->
        <div v-if="pageTitle && $route.path !== '/'" class="page-title-badge d-none d-lg-flex align-center ml-3">
          <v-divider vertical class="mr-3 my-2" style="height: 22px;"></v-divider>
          <span class="page-title-text">{{ pageTitle }}</span>
        </div>

        <!-- View Injected Custom Controls Slot (e.g. Appointments Calendar View Controls) -->
        <div v-if="$slots.default" class="header-slot-wrap d-flex align-center ml-2">
          <slot></slot>
        </div>
      </div>

      <!-- CENTER SECTION: Universal Pill Search (strictly centered in the topbar) -->
      <div v-if="!$slots.default" class="header-center-section d-none d-md-flex align-center justify-center">
        <div class="header-search-wrap d-flex align-center">
          <v-icon size="17" color="#94A3B8" class="mr-2">mdi-magnify</v-icon>
          <input
            ref="searchInput"
            type="text"
            v-model="searchQuery"
            placeholder="Search patients, MRN, bookings..."
            class="header-search-field"
            @keydown.enter="handleSearch"
          />
          <span class="search-shortcut-pill">Ctrl K</span>
        </div>
      </div>

      <!-- RIGHT SECTION: Notifications, Day/Night Switch, Language Switcher (Right-to-Left: Lang -> Theme -> Bell) -->
      <div class="header-right-section d-flex align-center ml-auto">
        <!-- 1. Notifications Bell (Leftmost) -->
        <v-menu
          offset-y
          transition="slide-y-transition"
          nudge-bottom="8"
          content-class="soon-menu-content"
          min-width="240"
          max-width="260"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              small
              class="header-icon-btn mr-3"
              v-bind="attrs"
              v-on="on"
              title="Notifications"
            >
              <v-badge
                color="#EF4444"
                dot
                overlap
                offset-x="4"
                offset-y="4"
                :value="unreadTotal > 0"
              >
                <v-icon size="19" :color="$vuetify.theme.dark ? '#94A3B8' : '#64748B'">
                  mdi-bell-outline
                </v-icon>
              </v-badge>
            </v-btn>
          </template>

          <v-card flat outlined class="rounded-xl pa-4 soon-menu-card text-center">
            <div class="soon-icon-wrap icon-wrap-bell mx-auto mb-2">
              <v-icon size="22" color="#0D9488" class="animated-bell-icon">mdi-bell-ring-outline</v-icon>
            </div>
            <div class="d-flex align-center justify-center mb-1">
              <span class="soon-status-tag">
                <span class="soon-pulse-dot"></span>
                In Development
              </span>
            </div>
            <div class="font-weight-bold text-subtitle-2 text--primary soon-heading mb-1">
              Notifications
            </div>
            <div class="text-caption text--secondary soon-message">
              Soon, we are working on it! Real-time alerts will be available here.
            </div>
          </v-card>
        </v-menu>

        <!-- 2. Day / Night Pill Switch (Center) -->
        <v-menu
          offset-y
          transition="slide-y-transition"
          nudge-bottom="8"
          content-class="soon-menu-content"
          min-width="240"
          max-width="260"
        >
          <template v-slot:activator="{ on, attrs }">
            <div
              class="day-night-toggle mr-3"
              :class="{ 'is-dark': $vuetify.theme.dark, 'is-light': !$vuetify.theme.dark }"
              v-bind="attrs"
              v-on="on"
              role="button"
              tabindex="0"
              title="Theme Switcher"
            >
              <div class="toggle-track">
                <!-- Light Mode Sun Disk -->
                <div class="sun-disk" v-if="!$vuetify.theme.dark">
                  <svg class="sun-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="5" fill="#F59E0B"/>
                    <path d="M12 2V4M12 20V22M4 12H2M22 12H20M5.64 5.64L4.22 4.22M19.78 19.78L18.36 18.36M5.64 18.36L4.22 19.78M19.78 4.22L18.36 5.64" stroke="#F59E0B" stroke-width="2.2" stroke-linecap="round"/>
                  </svg>
                </div>

                <!-- Dark Mode Moon & Star Disk -->
                <div class="moon-disk" v-else>
                  <svg class="moon-svg" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <!-- Crescent moon in white -->
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z" fill="#FFFFFF"/>
                    <!-- Yellow star in the crescent corner -->
                    <path d="M17.5 5.5L18.2 7.2L20 7.4L18.6 8.6L19 10.4L17.5 9.4L16 10.4L16.4 8.6L15 7.4L16.8 7.2L17.5 5.5Z" fill="#FACC15"/>
                  </svg>
                </div>
              </div>
            </div>
          </template>

          <v-card flat outlined class="rounded-xl pa-4 soon-menu-card text-center">
            <div class="soon-icon-wrap icon-wrap-theme mx-auto mb-2">
              <v-icon size="22" color="#F59E0B" class="animated-theme-icon">mdi-theme-light-dark</v-icon>
            </div>
            <div class="d-flex align-center justify-center mb-1">
              <span class="soon-status-tag tag-amber">
                <span class="soon-pulse-dot dot-amber"></span>
                In Development
              </span>
            </div>
            <div class="font-weight-bold text-subtitle-2 text--primary soon-heading mb-1">
              Theme Mode
            </div>
            <div class="text-caption text--secondary soon-message">
              Soon, we are working on it! Theme switching is currently being polished.
            </div>
          </v-card>
        </v-menu>

        <!-- 3. Language Switcher Pill (Rightmost) -->
        <v-menu
          offset-y
          left
          transition="slide-y-transition"
          nudge-bottom="8"
          content-class="soon-menu-content"
          min-width="240"
          max-width="260"
        >
          <template v-slot:activator="{ on, attrs }">
            <button
              type="button"
              class="lang-switch-pill d-flex align-center"
              v-bind="attrs"
              v-on="on"
              title="Language Switcher"
            >
              <v-icon size="16" class="lang-icon mr-2" color="#0D9488">mdi-translate</v-icon>
              <span class="lang-label">{{ $i18n.locale === 'ar' ? 'English' : 'العربية' }}</span>
            </button>
          </template>

          <v-card flat outlined class="rounded-xl pa-4 soon-menu-card text-center">
            <div class="soon-icon-wrap icon-wrap-lang mx-auto mb-2">
              <v-icon size="22" color="#0284C7" class="animated-lang-icon">mdi-translate</v-icon>
            </div>
            <div class="d-flex align-center justify-center mb-1">
              <span class="soon-status-tag tag-sky">
                <span class="soon-pulse-dot dot-sky"></span>
                In Development
              </span>
            </div>
            <div class="font-weight-bold text-subtitle-2 text--primary soon-heading mb-1">
              Language Switcher
            </div>
            <div class="text-caption text--secondary soon-message">
              Soon, we are working on it! Multi-language localization is underway.
            </div>
          </v-card>
        </v-menu>
      </div>
    </v-app-bar>

    <!-- Modern Breadcrumbs Bar for Nested Pages -->
    <v-breadcrumbs
      v-if="ShowBreadcrumbs && breadcrumbs.length > 0"
      :items="breadcrumbs"
      divider="›"
      class="clinical-breadcrumbs"
    >
      <template v-slot:item="{ item }">
        <v-breadcrumbs-item :to="item.to" :exact="item.exact">
          {{ item.text }}
        </v-breadcrumbs-item>
      </template>
    </v-breadcrumbs>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import api from "@/utils/api";

export default {
  name: "Header",
  props: {
    title: {
      type: String,
      default: "",
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      ShowBreadcrumbs: false,
      searchQuery: "",
      unreadTotal: 0,
      unreadPollInterval: null,
    };
  },
  computed: {
    ...mapGetters(["getSelectedBranchId", "getBranches"]),
    responsive() {
      return (
        this.$vuetify.breakpoint.name === "xs" ||
        this.$vuetify.breakpoint.name === "sm" ||
        window.innerWidth <= 1069
      );
    },
    pageTitle() {
      if (this.hideTitle || !this.title || this.title === "Dashboard" || this.title === this.$t("name")) {
        return "";
      }
      return this.title;
    },
    showBranchSelector() {
      return (
        this.$route.path === "/appointements" ||
        this.$route.path === "/" ||
        this.$route.path === "/statistics"
      );
    },
    currentUser() {
      return JSON.parse(localStorage.getItem("user") || "null");
    },
    canSeeAllBranches() {
      const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");
      return (
        permissions.includes("ADMINISTRATOR") ||
        permissions.includes("show_all_branches")
      );
    },
    userBranch() {
      return this.currentUser?.branch || null;
    },
    branches() {
      return this.getBranches || [];
    },
    branchItems() {
      return [{ id: null, name: "All Branches" }, ...this.branches];
    },
    selectedBranchId: {
      get() {
        return this.getSelectedBranchId;
      },
      set(val) {
        this.setSelectedBranchIdAction(val);
      },
    },
    currentBranchName() {
      if (!this.selectedBranchId) return "All Branches";
      const found = this.branches.find((b) => b.id === this.selectedBranchId);
      return found ? found.name : "All Branches";
    },
    breadcrumbs() {
      let pathArray = this.$route.path.split("/");
      pathArray.shift();

      let breadcrumbArray = [];
      let fullPath = "";

      pathArray.forEach((item) => {
        fullPath += "/" + item;
        const matched = this.$router.match(fullPath);
        if (matched && matched.meta && matched.meta.breadcrumb) {
          breadcrumbArray.push({
            text: matched.meta.breadcrumb,
            to: fullPath,
            exact: true,
          });
        }
      });
      return breadcrumbArray;
    },
  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalKeydown);
    this.fetchUnreadTotal();
    this.unreadPollInterval = setInterval(this.fetchUnreadTotal, 20000);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalKeydown);
    if (this.unreadPollInterval) {
      clearInterval(this.unreadPollInterval);
    }
  },
  methods: {
    ...mapActions(["setSelectedBranchIdAction", "fetchBranches"]),
    onClickBtn() {
      this.$store.dispatch("toggleDrawerAction");
    },
    onSelectBranch(branchId) {
      this.selectedBranchId = branchId;
      this.$emit("branch-changed", branchId);
    },
    toggleTheme() {
      this.$vuetify.theme.dark = !this.$vuetify.theme.dark;
      try {
        localStorage.setItem("theme_dark", this.$vuetify.theme.dark ? "true" : "false");
      } catch (e) {
        // Ignore localStorage quota errors
      }
    },
    toggleLanguage() {
      const current = this.$i18n.locale;
      const next = current === "ar" ? "en" : "ar";
      this.$i18n.locale = next;
      try {
        localStorage.setItem("locale", next);
      } catch (e) {}
      if (typeof document !== "undefined") {
        document.dir = next === "ar" ? "rtl" : "ltr";
      }
      if (this.$vuetify) {
        this.$vuetify.rtl = next === "ar";
      }
    },
    handleSearch() {
      if (this.searchQuery && this.searchQuery.trim()) {
        const query = this.searchQuery.trim();
        this.$router
          .push({
            path: "/patients",
            query: { search: query },
          })
          .catch(() => {});
      }
    },
    handleGlobalKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        const input = this.$refs.searchInput;
        if (input) {
          input.focus();
          input.select();
        }
      }
    },
    async fetchUnreadTotal() {
      try {
        const res = await api.get("/api/conversations/unread-total");
        if (typeof res.data === "number") {
          this.unreadTotal = res.data;
        }
      } catch (e) {
        // Ignore silent polling error
      }
    },
  },
  watch: {
    showBranchSelector: {
      immediate: true,
      handler(val) {
        if (val && this.canSeeAllBranches && this.branches.length === 0) {
          this.fetchBranches();
        }
      },
    },
  },
};
</script>

<style scoped>
.clinical-header-wrapper {
  position: relative;
  z-index: 10;
}

.clinical-topbar {
  border-bottom: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  padding: 0 24px;
}

.theme--dark .clinical-topbar {
  background-color: #131B2E !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
}

/* ============================================================
   1. IMPROVED BRANCH SELECTOR
   ============================================================ */
.branch-selector-pill {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 5px 12px 5px 8px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.branch-selector-pill:hover {
  background-color: #F0FDFA;
  border-color: rgba(13, 148, 136, 0.4);
  box-shadow: 0 2px 6px rgba(13, 148, 136, 0.12);
}

.theme--dark .branch-selector-pill {
  background-color: #0B0F19;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.theme--dark .branch-selector-pill:hover {
  background-color: #111726;
  border-color: rgba(45, 212, 191, 0.35);
}

.branch-icon-box {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background-color: #F0FDFA;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.branch-selector-pill:hover .branch-icon-box {
  background-color: #CCFBF1;
}

.theme--dark .branch-icon-box {
  background-color: rgba(45, 212, 191, 0.12);
}

.theme--dark .branch-selector-pill:hover .branch-icon-box {
  background-color: rgba(45, 212, 191, 0.2);
}

.branch-eyebrow {
  font-size: 8px;
  font-weight: 700;
  color: #94A3B8;
  letter-spacing: 0.6px;
  line-height: 1;
}

.branch-name {
  font-size: 12.5px;
  font-weight: 700;
  color: #0F172A;
  line-height: 1.25;
  max-width: 170px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.theme--dark .branch-name {
  color: #F8FAFC;
}

.branch-chevron-box {
  display: flex;
  align-items: center;
}

/* Branch Menu Dropdown Card */
.branch-menu-card {
  background-color: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  min-width: 240px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.12), 0 8px 10px -6px rgba(0, 0, 0, 0.08) !important;
}

.theme--dark .branch-menu-card {
  background-color: #162238 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.branch-menu-item {
  transition: all 0.15s ease;
}

.branch-menu-item:hover {
  background-color: #F1F5F9 !important;
}

.theme--dark .branch-menu-item:hover {
  background-color: rgba(255, 255, 255, 0.06) !important;
}

.active-branch-item {
  background-color: #F0FDFA !important;
}

.theme--dark .active-branch-item {
  background-color: rgba(45, 212, 191, 0.14) !important;
}

.branch-item-icon-wrap {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background-color: #F1F5F9;
  display: flex;
  align-items: center;
  justify-content: center;
}

.branch-item-icon-wrap.active-wrap {
  background-color: #CCFBF1;
}

.theme--dark .branch-item-icon-wrap {
  background-color: #1E293B;
}

.theme--dark .branch-item-icon-wrap.active-wrap {
  background-color: rgba(45, 212, 191, 0.2);
}

.branch-item-title {
  font-size: 13px !important;
  color: #1E293B;
}

.theme--dark .branch-item-title {
  color: #F8FAFC;
}

.active-branch-item .branch-item-title {
  color: #0D9488 !important;
}

.theme--dark .active-branch-item .branch-item-title {
  color: #2DD4BF !important;
}

/* ============================================================
   2. CENTERED UNIVERSAL SEARCH BAR
   ============================================================ */
.header-center-section {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  max-width: 440px;
  pointer-events: none;
}

.header-search-wrap {
  pointer-events: auto;
  position: relative;
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 9999px;
  height: 40px;
  padding: 0 14px;
  width: 100%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.header-search-wrap:focus-within {
  background-color: #FFFFFF;
  border-color: #0D9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.18);
}

.theme--dark .header-search-wrap {
  background-color: #0B0F19;
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
}

.theme--dark .header-search-wrap:focus-within {
  background-color: #0B0F19;
  border-color: #2DD4BF;
  box-shadow: 0 0 0 3px rgba(45, 212, 191, 0.25);
}

.header-search-field {
  border: none;
  outline: none;
  background: transparent;
  width: 100%;
  font-size: 12.5px;
  color: #0F172A;
}

.theme--dark .header-search-field {
  color: #F8FAFC;
}

.header-search-field::placeholder {
  color: #94A3B8;
  font-size: 12.5px;
}

.search-shortcut-pill {
  font-size: 10px;
  font-weight: 700;
  color: #64748B;
  background-color: #FFFFFF;
  border: 1px solid #CBD5E1;
  border-radius: 5px;
  padding: 2px 7px;
  white-space: nowrap;
  letter-spacing: 0.3px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.theme--dark .search-shortcut-pill {
  background-color: #1E293B;
  color: #94A3B8;
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: none;
}

/* ============================================================
   3. CUSTOM DAY / NIGHT PILL SWITCH (Matching attached image)
   ============================================================ */
.day-night-toggle {
  width: 60px;
  height: 30px;
  border-radius: 9999px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.3s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s ease;
  user-select: none;
  display: flex;
  align-items: center;
  outline: none;
  padding: 3px;
  box-sizing: border-box;
}

.day-night-toggle:hover {
  filter: brightness(1.05);
}

.day-night-toggle.is-light {
  background-color: #f8fafc;
  border: 1px solid #E2E8F0;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.05);
}

.day-night-toggle.is-dark {
  background-color: #0B0F19;
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.4);
}

.toggle-track {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
}

/* Sun disk (positioned on left) */
.sun-disk {
  position: absolute;
  left: 1px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.sun-svg {
  width: 22px;
  height: 22px;
}

/* Moon disk (positioned on right) */
.moon-disk {
  position: absolute;
  right: 1px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.moon-svg {
  width: 20px;
  height: 20px;
}

/* ============================================================
   4. LANGUAGE SWITCHER PILL
   ============================================================ */
.lang-switch-pill {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  height: 30px;
  box-sizing: border-box;
  padding: 0 11px;
  font-size: 12px;
  font-weight: 700;
  color: #0F172A;
  cursor: pointer;
  transition: all 0.15s ease;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.lang-switch-pill:hover {
  background-color: #F0FDFA;
  border-color: rgba(13, 148, 136, 0.35);
  color: #0D9488;
}

.theme--dark .lang-switch-pill {
  background-color: #0B0F19;
  border-color: rgba(255, 255, 255, 0.08);
  color: #F8FAFC;
}

.theme--dark .lang-switch-pill:hover {
  background-color: #111726;
  border-color: rgba(45, 212, 191, 0.35);
  color: #2DD4BF;
}

.lang-label {
  letter-spacing: 0.2px;
}

/* ============================================================
   5. NOTIFICATIONS & BREADCRUMBS
   ============================================================ */
.header-icon-btn {
  border-radius: 8px;
  transition: background-color 0.15s ease;
}

.header-icon-btn:hover {
  background-color: #F1F5F9;
}

.theme--dark .header-icon-btn:hover {
  background-color: #1E293B;
}

.notification-popup-card {
  background-color: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
}

.theme--dark .notification-popup-card {
  background-color: #162238 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

/* ============================================================
   6. COMING SOON POPUP MENUS
   ============================================================ */
.soon-menu-content {
  border-radius: 16px !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08), 0 8px 10px -6px rgba(0, 0, 0, 0.04) !important;
}

.soon-menu-card {
  background-color: #FFFFFF !important;
  border: 1px solid #E2E8F0 !important;
  box-sizing: border-box;
}

.soon-icon-wrap {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-wrap-bell {
  background-color: #F0FDFA;
  border: 1px solid #CCFBF1;
}

.icon-wrap-theme {
  background-color: #FFFBEB;
  border: 1px solid #FEF3C7;
}

.icon-wrap-lang {
  background-color: #F0F9FF;
  border: 1px solid #BAE6FD;
}

/* Animations */
.animated-bell-icon {
  animation: bellSwing 2.4s ease-in-out infinite;
  transform-origin: top center;
}

@keyframes bellSwing {
  0%, 65%, 100% {
    transform: rotate(0deg);
  }
  5% {
    transform: rotate(18deg);
  }
  15% {
    transform: rotate(-16deg);
  }
  25% {
    transform: rotate(12deg);
  }
  35% {
    transform: rotate(-8deg);
  }
  45% {
    transform: rotate(4deg);
  }
  55% {
    transform: rotate(0deg);
  }
}

.animated-theme-icon {
  animation: themeRotate 3.5s ease-in-out infinite;
}

@keyframes themeRotate {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.12);
  }
  100% {
    transform: rotate(360deg) scale(1);
  }
}

.animated-lang-icon {
  animation: langBob 2.6s ease-in-out infinite;
}

@keyframes langBob {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-4px) scale(1.1);
  }
}

/* Status Tag & Pulsing Dot */
.soon-status-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 9999px;
  padding: 2px 8px;
  letter-spacing: 0.2px;
  background-color: #F0FDFA;
  color: #0F766E;
  border: 1px solid #CCFBF1;
}

.soon-status-tag.tag-amber {
  background-color: #FFFBEB;
  color: #92400E;
  border-color: #FEF3C7;
}

.soon-status-tag.tag-sky {
  background-color: #F0F9FF;
  color: #0369A1;
  border-color: #BAE6FD;
}

.soon-pulse-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 5px;
  display: inline-block;
  background-color: #0D9488;
  animation: pulseDotTeal 1.8s infinite;
}

.soon-pulse-dot.dot-amber {
  background-color: #D97706;
  animation: pulseDotAmber 1.8s infinite;
}

.soon-pulse-dot.dot-sky {
  background-color: #0284C7;
  animation: pulseDotSky 1.8s infinite;
}

@keyframes pulseDotTeal {
  0% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.4); }
  70% { box-shadow: 0 0 0 5px rgba(13, 148, 136, 0); }
  100% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0); }
}

@keyframes pulseDotAmber {
  0% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0.4); }
  70% { box-shadow: 0 0 0 5px rgba(217, 119, 6, 0); }
  100% { box-shadow: 0 0 0 0 rgba(217, 119, 6, 0); }
}

@keyframes pulseDotSky {
  0% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0.4); }
  70% { box-shadow: 0 0 0 5px rgba(2, 132, 199, 0); }
  100% { box-shadow: 0 0 0 0 rgba(2, 132, 199, 0); }
}

.soon-heading {
  font-size: 0.9rem !important;
  line-height: 1.2;
}

.soon-message {
  font-size: 0.76rem !important;
  line-height: 1.45;
  color: #64748B;
}

/* Dark Mode Overrides */
.theme--dark .soon-menu-card {
  background-color: #131B2E !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
}

.theme--dark .soon-message {
  color: #94A3B8 !important;
}

.theme--dark .icon-wrap-bell {
  background-color: rgba(45, 212, 191, 0.12) !important;
  border-color: rgba(45, 212, 191, 0.25) !important;
}

.theme--dark .icon-wrap-theme {
  background-color: rgba(245, 158, 11, 0.12) !important;
  border-color: rgba(245, 158, 11, 0.25) !important;
}

.theme--dark .icon-wrap-lang {
  background-color: rgba(56, 189, 248, 0.12) !important;
  border-color: rgba(56, 189, 248, 0.25) !important;
}

.theme--dark .soon-status-tag {
  background-color: rgba(45, 212, 191, 0.15) !important;
  color: #2DD4BF !important;
  border-color: rgba(45, 212, 191, 0.3) !important;
}

.theme--dark .soon-status-tag.tag-amber {
  background-color: rgba(217, 119, 6, 0.15) !important;
  color: #FBBF24 !important;
  border-color: rgba(217, 119, 6, 0.3) !important;
}

.theme--dark .soon-status-tag.tag-sky {
  background-color: rgba(56, 189, 248, 0.15) !important;
  color: #38BDF8 !important;
  border-color: rgba(56, 189, 248, 0.3) !important;
}

.clinical-breadcrumbs {
  padding: 8px 24px;
  background-color: transparent;
  font-size: 12.5px;
}

.page-title-text {
  font-size: 13.5px;
  font-weight: 700;
  color: #0F172A;
  white-space: nowrap;
}

.theme--dark .page-title-text {
  color: #F8FAFC;
}

/* Mobile responsive adjustments */
@media (max-width: 600px) {
  .clinical-topbar {
    padding: 0 12px !important;
  }
  .lang-switch-pill {
    height: 28px !important;
    padding: 0 8px !important;
    font-size: 11px !important;
  }
  .day-night-toggle {
    width: 52px !important;
    height: 28px !important;
  }
  .sun-disk, .moon-disk {
    width: 20px !important;
    height: 20px !important;
  }
}
</style>
